import { formatMoney } from "@shopify/theme-currency";
import { html, LitElement } from "lit";
import { customElement, property } from "lit/decorators.js";

export interface shopifyProduct {
    id: number;
    title: string;
    handle: string;
    description: string;
    published_at: Date;
    created_at: Date;
    vendor: string;
    type: string;
    tags: string[];
    price: number;
    price_min: number;
    price_max: number;
    available: boolean;
    price_varies: boolean;
    compare_at_price: number;
    compare_at_price_min: number;
    compare_at_price_max: number;
    compare_at_price_varies: boolean;
    variants: Variant[];
    images: string[];
    featured_image: string;
    options: string[];
    media: Media[];
    requires_selling_plan: boolean;
    selling_plan_groups: any[];
    content: string;
}

export interface Media {
    alt: string;
    id: number;
    position: number;
    preview_image: PreviewImage;
    aspect_ratio: number;
    height: number;
    media_type: string;
    src: string;
    width: number;
}

export interface PreviewImage {
    aspect_ratio: number;
    height: number;
    width: number;
    src: string;
}

export interface Variant {
    id: number;
    title: string;
    option1: string;
    option2: null;
    option3: null;
    sku: string;
    requires_shipping: boolean;
    taxable: boolean;
    featured_image: FeaturedImage;
    available: boolean;
    name: string;
    public_title: string;
    options: string[];
    price: number;
    weight: number;
    compare_at_price: number;
    inventory_management: string;
    barcode: string;
    featured_media: FeaturedMedia;
    requires_selling_plan: boolean;
    selling_plan_allocations: any[];
    quantity_rule: QuantityRule;
}

export interface FeaturedImage {
    id: number;
    product_id: number;
    position: number;
    created_at: Date;
    updated_at: Date;
    alt: string;
    width: number;
    height: number;
    src: string;
    variant_ids: number[];
}

export interface FeaturedMedia {
    alt: string;
    id: number;
    position: number;
    preview_image: PreviewImage;
}

export interface QuantityRule {
    min: number;
    max: null;
    increment: number;
}

@customElement("product-card")
export class ProductCard extends LitElement {
    @property()
    data: string;
    @property()
    show_product_details: string;
    @property()
    show_product_title: string;
    @property()
    available_siblings: string;
    @property()
    sibling_ids: string;
    @property()
    loading_gif: string;

    productData: shopifyProduct;
    siblingIds: [];
    clickedButtonOpenClass = "btn-quick-buy-toggle--open";

    createRenderRoot() {
        return this;
    }

    generateFlexibleProductImage = (src, isFlipImage = false) => {
        const srcsetMedia = [
            {
                screenSize: 425,
                imageSize: 190,
            },
            {
                screenSize: 768,
                imageSize: 240,
            },
            {
                screenSize: 1440,
                imageSize: 320,
            },
            {
                screenSize: 1920,
                imageSize: 480,
            },
        ];
        const [imageUrl, version] = src.split("?");
        const extensionIndex = imageUrl.lastIndexOf(".");
        const imageLinkWithoutExtensions = imageUrl.slice(0, extensionIndex);
        const extension = imageUrl.slice(extensionIndex);

        const imageSources = srcsetMedia.map((media) => {
            const alteredImgLink = imageLinkWithoutExtensions
                .concat(`_${media.imageSize}x`)
                .concat(extension)
                .concat(`?${version}`);

            return `<source srcset="${alteredImgLink}" width="${media.imageSize}" media="(max-width: ${media.screenSize}px)"/>`;
        });

        const imageSrcAttr = imageLinkWithoutExtensions
            .concat(`_375x`)
            .concat(extension)
            .concat(`?${version}`);

        return `
      <picture class="${isFlipImage ? "card-product-item-flip-image tw-hidden group-hover:tw-block" : "card-product-item-main-image group-hover:tw-hidden"}">
        ${imageSources.join("")}
        <img src="${imageSrcAttr}" class="lazyload" loading="lazy">
      </picture>
    `;
    };

    handleQuickBuyToggleClick = (event) => {
        this.openOrCloseQuickBuy(event.target);
    };

    handleQuickBuyCloseClick = (event) => {
        this.openOrCloseQuickBuy(
            event.target
                .closest(".quick-buy-container")
                .querySelector(".btn-quick-buy-toggle"),
        );
    };

    openOrCloseQuickBuy = (clickedButtonElement) => {
        if (
            clickedButtonElement.classList.contains(this.clickedButtonOpenClass)
        ) {
            this.closeQuickBuy(clickedButtonElement);
        } else {
            this.openQuickBuy(clickedButtonElement);
        }
    };

    openQuickBuy = (clickedButtonElement) => {
        // Close other open quick buy buttons
        document
            .querySelectorAll(".btn-quick-buy-toggle")
            .forEach((quickBuyToggleButton) => {
                if (quickBuyToggleButton != clickedButtonElement)
                    this.closeQuickBuy(quickBuyToggleButton);
            });

        // Open this quick buy button
        clickedButtonElement.classList.add(this.clickedButtonOpenClass);
        clickedButtonElement.textContent =
            clickedButtonElement.textContent.replace("+", "-");
        const quickBuyDetailsElement = clickedButtonElement
            .closest(".quick-buy-container")
            ?.querySelector(".quick-buy-details");
        if (quickBuyDetailsElement) {
            quickBuyDetailsElement.classList.add("tw-flex");
            quickBuyDetailsElement.classList.remove("tw-hidden");

            if (clickedButtonElement.dataset.product_id) {
                let relatedProductIds = "";
                if (clickedButtonElement.dataset.related_products)
                    relatedProductIds =
                        clickedButtonElement.dataset.related_products;

                const quickBuyOptionsElement =
                    quickBuyDetailsElement.querySelector(".quick-buy-options");
                if (!quickBuyOptionsElement)
                    this.addQuickBuyDetails(
                        quickBuyDetailsElement,
                        clickedButtonElement.dataset.product_id,
                        relatedProductIds,
                    );
            }
        }
    };

    closeQuickBuy = (clickedButtonElement) => {
        clickedButtonElement.classList.remove(this.clickedButtonOpenClass);
        clickedButtonElement.textContent =
            clickedButtonElement.textContent.replace("-", "+");
        const quickBuyDetailsElement = clickedButtonElement
            .closest(".quick-buy-container")
            ?.querySelector(".quick-buy-details");
        if (quickBuyDetailsElement) {
            quickBuyDetailsElement.classList.add("tw-hidden");
            quickBuyDetailsElement.classList.remove("tw-flex");
        }
    };

    addQuickBuyDetails = async (
        quickBuyDetailsElement,
        mainProductId,
        relatedProductIds,
    ) => {
        const quickBuyProductsData =
            await this.fetchQuickBuyProductsData(relatedProductIds);

        const oldQuickBuyOptionsElement =
            quickBuyDetailsElement.querySelector(".quick-buy-options");
        if (oldQuickBuyOptionsElement) oldQuickBuyOptionsElement.remove();

        const quickBuyOptionsElement = document.createElement("div");
        quickBuyOptionsElement.classList.add("quick-buy-options");

        let quickBuyCurrentlyActiveProduct = null;

        quickBuyProductsData.lists.forEach((productList) => {
            const productListElement = document.createElement("div");
            productListElement.classList.add(
                "quick-buy-options-list",
                "tw-mb-4",
            );

            if (quickBuyProductsData.totalProductsAvailable === 1)
                productListElement.classList.add("tw-hidden");

            if (productList.products.length > 0) {
                const listTitleElement = document.createElement("p");
                listTitleElement.textContent = productList.title;
                listTitleElement.classList.add("tw-text-sm");
                productListElement.append(listTitleElement);

                const productListOptionElement = document.createElement("div");
                productListOptionElement.classList.add(
                    "quick-buy-swatches",
                    "tw-flex",
                    "tw-flex-wrap",
                    "tw-flex-row",
                    "tw-justify-start",
                    "tw-content-center",
                    "tw-items-center",
                    "tw-gap-4",
                    "tw-mt-2",
                );

                productList.products.forEach((product) => {
                    const productSwatchElement = document.createElement("div");
                    productSwatchElement.classList.add(
                        "quick-buy-swatch",
                        "tw-block",
                        "tw-h-6",
                        "tw-w-6",
                        "tw-border",
                        "tw-border-solid",
                        "tw-rounded-full",
                        "tw-shadow-[inset_0_0_0_2px_#fff]",
                        "tw-bg-center",
                        "tw-bg-no-repeat",
                        "tw-bg-cover",
                    );

                    if (product.tags) {
                        const productColourTags = product.tags.filter(
                            (productTag) => productTag.includes("Colour:"),
                        );
                        if (productColourTags.length) {
                            const productColourImageFile = `${this.handleizeString(productColourTags[0].replace("Colour:", ""))}.jpg`;

                            productSwatchElement.style.backgroundImage = `url('${window.fileBaseUrl}${productColourImageFile}')`;
                        }
                    }

                    if (product.images.nodes) {
                        let productMainImage = "";
                        let productSwapImage = "";
                        if (product.images.nodes[0]) {
                            productMainImage = product.images.nodes[0].src;
                            productSwapImage = product.images.nodes[0].src;
                        }
                        if (product.images.nodes[1]) {
                            productSwapImage = product.images.nodes[1].src;
                        }
                        productSwatchElement.dataset.productMainImage =
                            productMainImage;
                        productSwatchElement.dataset.productSwapImage =
                            productSwapImage;
                    }

                    if (product.variants.nodes)
                        productSwatchElement.dataset.productVariants =
                            JSON.stringify(product.variants.nodes);

                    const productID = product.id.split("/").pop();
                    if (productID === mainProductId) {
                        productSwatchElement.classList.add(
                            "quick-buy-swatch--active",
                            "tw-border-[#666666]",
                        );
                        quickBuyCurrentlyActiveProduct = product;
                    } else {
                        productSwatchElement.classList.add(
                            "tw-border-[#c4c4c4]",
                            "tw-cursor-pointer",
                        );
                    }

                    productSwatchElement.addEventListener(
                        "click",
                        this.handleQuickBuySwatchElementClick,
                    );

                    productListOptionElement.append(productSwatchElement);
                });
                productListElement.append(productListOptionElement);

                quickBuyOptionsElement.append(productListElement);
            }
        });

        const quickBuySizesListElement = document.createElement("div");
        quickBuySizesListElement.classList.add("quick-buy-options-list");

        let quickBuySizesTitleText = "Select a size:";
        if (quickBuyCurrentlyActiveProduct) {
            if (quickBuyCurrentlyActiveProduct.options) {
                const quickBuyCurrentlyActiveProductDenominationsOptions =
                    quickBuyCurrentlyActiveProduct.options.filter(
                        (option) => option.name === "denomination",
                    );
                if (
                    quickBuyCurrentlyActiveProductDenominationsOptions.length >
                    0
                )
                    quickBuySizesTitleText = "Value:";
            }
        }

        const quickBuySizesTitleElement = document.createElement("p");
        quickBuySizesTitleElement.textContent = quickBuySizesTitleText;
        quickBuySizesTitleElement.classList.add("tw-text-sm");
        quickBuySizesListElement.append(quickBuySizesTitleElement);

        const quickBuySizesElement = document.createElement("div");
        quickBuySizesElement.classList.add(
            "quick-buy-sizes",
            "tw-flex",
            "tw-flex-wrap",
            "tw-flex-row",
            "tw-justify-start",
            "tw-content-center",
            "tw-items-center",
            "tw-gap-1",
            "-tw-ml-1",
            "tw-mt-1",
        );
        quickBuySizesListElement.append(quickBuySizesElement);

        quickBuyOptionsElement.append(quickBuySizesListElement);

        const quickBuyLoadingElement =
            quickBuyDetailsElement.querySelector(".quick-buy-loading");
        quickBuyLoadingElement.classList.add("tw-hidden");
        quickBuyLoadingElement.classList.remove("tw-block");

        const quickBuyOptionsWrapper = quickBuyDetailsElement.querySelector(
            ".quick-buy-options-wrapper",
        );
        if (quickBuyOptionsWrapper)
            quickBuyOptionsWrapper.append(quickBuyOptionsElement);

        if (quickBuyCurrentlyActiveProduct) {
            if (quickBuyCurrentlyActiveProduct.variants.nodes)
                await this.updateQuickBuyVariants(
                    quickBuyDetailsElement,
                    quickBuyCurrentlyActiveProduct.variants.nodes,
                );
        }
    };

    fetchQuickBuyProductsData = async (relatedProductIds) => {
        let relatedProductsGraphQuery = "";

        relatedProductIds.split(",").forEach((productId, index) => {
            relatedProductsGraphQuery += `product${index}: product(id: "${productId}") {
        availableForSale
        title
        id
        tags
        totalInventory
        options(first:10) {
          name
        }
        images(first:2) {
          nodes {
            src
          }
        }
        variants(first: 20) {
          nodes {
            id
            title
            availableForSale
          }
        }
      }`;
        });

        const options = {
            method: "POST",
            headers: new Headers({
                "content-type": "application/json",
                "X-Shopify-Storefront-Access-Token":
                    window.gqlProductInfoStorefrontAccessToken,
            }),
            body: JSON.stringify({
                query: `
          query products {
            ${relatedProductsGraphQuery}
          }
        `,
            }),
        };

        const relatedProductsRequest = await fetch(
            "/api/2023-04/graphql.json",
            options,
        );
        const relatedProductsData = await relatedProductsRequest.json();

        let lowStockThreshold = 5;
        if (window.theme.lowStockItemThreshold)
            lowStockThreshold = window.theme.lowStockItemThreshold;

        const productsLists = {
            totalProductsAvailable: Object.values(
                relatedProductsData.data,
            ).filter((product) => product && product.availableForSale).length,
            lists: [
                (this.highStockProducts = {
                    title: "Select Colour:",
                    products: Object.values(relatedProductsData.data).filter(
                        (product) =>
                            product &&
                            product.availableForSale &&
                            product.totalInventory > lowStockThreshold,
                    ),
                }),
                (this.lowStockProducts = {
                    title: "Limited Quantity:",
                    products: Object.values(relatedProductsData.data).filter(
                        (product) =>
                            product &&
                            product.availableForSale &&
                            product.totalInventory <= lowStockThreshold,
                    ),
                }),
            ],
        };

        return productsLists;
    };

    handleQuickBuySwatchElementClick = async (event) => {
        const clickedSwatchElement = event.target;

        const allSwatchElements = clickedSwatchElement
            .closest(".quick-buy-swatches")
            .querySelectorAll(".quick-buy-swatch");

        if (allSwatchElements) {
            allSwatchElements.forEach((swatchElement) => {
                if (swatchElement === clickedSwatchElement) {
                    swatchElement.classList.add(
                        "quick-buy-swatch--active",
                        "tw-border-[#666666]",
                    );
                    swatchElement.classList.remove(
                        "tw-border-[#c4c4c4]",
                        "tw-cursor-pointer",
                    );
                } else {
                    swatchElement.classList.remove(
                        "quick-buy-swatch--active",
                        "tw-border-[#666666]",
                    );
                    swatchElement.classList.add(
                        "tw-border-[#c4c4c4]",
                        "tw-cursor-pointer",
                    );
                }
            });
        }

        const quickBuyDetailsElement = clickedSwatchElement
            .closest(".quick-buy-container")
            .querySelector(".quick-buy-details");
        if (
            quickBuyDetailsElement &&
            clickedSwatchElement.dataset.productVariants
        ) {
            await this.updateQuickBuyVariants(
                quickBuyDetailsElement,
                JSON.parse(clickedSwatchElement.dataset.productVariants),
            );
        }

        if (clickedSwatchElement.dataset.productMainImage) {
            const swatchProductMainImageElement = clickedSwatchElement
                .closest(".card-product-item")
                ?.querySelector(".card-product-item-main-image");
            if (swatchProductMainImageElement) {
                swatchProductMainImageElement.outerHTML =
                    this.generateFlexibleProductImage(
                        clickedSwatchElement.dataset.productMainImage,
                    );
            }
        }

        if (clickedSwatchElement.dataset.productSwapImage) {
            const swatchProductSwapImageElement = clickedSwatchElement
                .closest(".card-product-item")
                ?.querySelector(".card-product-item-flip-image");
            if (swatchProductSwapImageElement) {
                swatchProductSwapImageElement.outerHTML =
                    this.generateFlexibleProductImage(
                        clickedSwatchElement.dataset.productSwapImage,
                        true,
                    );
            }
        }
    };

    updateQuickBuyVariants = async (quickBuyDetailsElement, newVariants) => {
        const quickBuySizesElement =
            quickBuyDetailsElement.querySelector(".quick-buy-sizes");

        if (quickBuySizesElement) {
            quickBuySizesElement.innerHTML = "";

            if (newVariants) {
                newVariants.forEach((variant) => {
                    const newVariantElement = document.createElement("button");
                    newVariantElement.classList.add(
                        "tw-block",
                        "tw-py-1",
                        "tw-px-2",
                        "tw-border",
                        "tw-border-solid",
                        "tw-border-black",
                        "tw-border-opacity-0",
                    );

                    if (variant.id)
                        newVariantElement.dataset.variantId =
                            variant.id.replace(
                                "gid://shopify/ProductVariant/",
                                "",
                            );
                    if (variant.title)
                        newVariantElement.textContent = variant.title;

                    if (variant.availableForSale) {
                        newVariantElement.classList.add(
                            "tw-text-black",
                            "hover:tw-border-opacity-100",
                            "focus:tw-border-opacity-100",
                        );
                    } else {
                        newVariantElement.disabled = true;
                        newVariantElement.classList.add(
                            "tw-text-[#666666]",
                            "tw-line-through",
                        );
                    }

                    newVariantElement.addEventListener(
                        "click",
                        this.handleQuickBuyVariantClick,
                    );

                    quickBuySizesElement.append(newVariantElement);
                });
            }
        }
    };

    handleQuickBuyVariantClick = async (event) => {
        const clickedVariant = event.target;
        if (clickedVariant.dataset.variantId) {
            await CartJS.addItem(clickedVariant.dataset.variantId, 1, null, {
                success: () => {
                    const associatedQuickBuyButtonElement = clickedVariant
                        .closest(".quick-buy-container")
                        .querySelector(".btn-quick-buy-toggle");

                    if (associatedQuickBuyButtonElement)
                        this.closeQuickBuy(associatedQuickBuyButtonElement);
                },
            });
        }
    };

    handleizeString = (string) => {
        let handleizedString = string.toLowerCase();

        const toReplace = ['"', "'", "\\", "(", ")", "[", "]"];
        for (let i = 0; i < toReplace.length; ++i) {
            handleizedString = handleizedString.replace(toReplace[i], "");
        }

        handleizedString = handleizedString.replace(/\W+/g, "-");
        if (handleizedString.charAt(handleizedString.length - 1) == "-")
            handleizedString = handleizedString.replace(/-+z/, "");
        if (handleizedString.charAt(0) == "-")
            handleizedString = handleizedString.replace(/A-+/, "");

        return handleizedString;
    };

    render() {
        this.productData = JSON.parse(window.atob(this.data));
        this.siblingIds = JSON.parse(window.atob(this.sibling_ids));

        return html`<div class="card-product-item">
      <div class="tw-group tw-relative">
        <a href="/products/${this.productData.handle}">
          <img
            class="group-hover:tw-hidden card-product-item-main-image"
            src="${this.productData.images[1]}"
          />
          <img
            class="tw-hidden group-hover:tw-block card-product-item-flip-image"
            src="${this.productData.images[0]}"
          />
        </a>

        <div>
          <div class="quick-buy-container tw-relative tw-height-0" aria-hidden="true">
            <button
              type="button"
              data-product_id="${this.productData.id}"
              data-related_products="${this.siblingIds.toString()}"
              class="btn-quick-buy-toggle tw-absolute tw-bottom-5 tw-right-[0.65rem] tw-z-10 tw-h-[1.875rem] tw-px-2 tw-text-xs tw-leading-[1.5625rem] tw-border tw-border-solid tw-border-[#b6b6b6b3] tw-rounded-md tw-bg-[#f9f9f9] tw-cursor-pointer"
              tabindex="-1"
              @click="${this.handleQuickBuyToggleClick}"
            >
              Add +
            </button>


            <div class="quick-buy-details tw-hidden tw-flex-nowrap tw-flex-col tw-justify-start tw-content-start tw-items-start tw-absolute -tw-top-[0.9rem] tw-left-2 tw-right-2 tw-z-10 tw-min-h-[2.5rem] tw-rounded-md tw-bg-[#f9f9f9] tw-shadow-[0_0.1875rem_0.1875rem_rgba(0,0,0,.5)]">
              <div class="quick-buy-options-wrapper tw-relative tw-p-5 tw-flex flex-nowrap flex-col justify-center content-start items-start tw-w-full ${this.show_product_details ? "tw-max-h-28 tw-overflow-y-scroll" : ""}">
                <button type="button" class="btn-quick-buy-close tw-absolute tw-top-1 tw-right-1 tw-p-2 overflow-hidden"
                @click="${this.handleQuickBuyCloseClick}">
                  <svg aria-hidden="true" focusable="false" role="presentation" class="icon icon-close icon--stroke-based tw-block tw-w-4 tw-h-4" width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M19.5 5L5 19.5" stroke="black" stroke-width="1.71429" stroke-linecap="round" stroke-linejoin="round"/>
                    <path d="M5 5L19.5 19.5" stroke="black" stroke-width="1.71429" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                </button>

                <img class="quick-buy-loading tw-self-center tw-my-4 tw-mx-auto" src="${this.loading_gif}" alt="Loading">
              </div>
            </div>
          </div>

        </div>
      </div>

      ${
          this.show_product_details == "true"
              ? html`
            <a href="/products/${this.productData.handle}" >
              <div class="tw-flex tw-justify-between tw-pt-3">
                <div class="tw-text-sm">
                  <div class="tw-font-medium tw-pb-1 tw-max-w-[90%]">
                    ${this.productData.title}
                  </div>
                  <div class="tw-pb-1">
                    ${formatMoney(this.productData.price)}
                  </div>
                </div>
                <div>
                  <p
                    data-product-id="${this.productData.id}"
                    class="clp-fav-btn show-blank"
                  >
                    <button
                      class="blank-fav-icon open-modal"
                      data-modal-type="show-list-selection"
                      data-variant-id="${this.productData.variants[0]?.id}"
                      data-product-id="${this.productData.id}"
                      data-product-url="${
                          "https://" +
                          window.location.host +
                          "/products/" +
                          this.productData.handle
                      }"
                      data-product-has-size-variant="true"
                      data-product-has-color-variant="false"
                      data-save-size-variant="false"
                    >
                      <svg
                        aria-hidden="true"
                        focusable="false"
                        role="presentation"
                        class="tw-pointer-events-none icon icon-heart icon--stroke-based"
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M12.0057 19.5216L4.36699 12.6025C0.21554 8.45103 6.31817 0.48025 12.0057 6.92884C17.6931 0.48025 23.7681 8.47871 19.6443 12.6025L12.0057 19.5216Z"
                          stroke="black"
                          stroke-width="1.71429"
                          stroke-linecap="round"
                          stroke-linejoin="round"
                        ></path>
                      </svg>
                    </button>
                    <button
                      class="filled-fav-icon remove-product-from-wishlist"
                      data-variant-id="${this.productData.variants[0]?.id}"
                      data-product-id="${this.productData.id}"
                      data-product-url="${
                          "https://" +
                          window.location.host +
                          "/products/" +
                          this.productData.handle
                      }"
                      data-product-has-size-variant="true"
                      data-product-has-color-variant="false"
                      data-save-size-variant="false"
                    >
                      <svg
                        aria-hidden="true"
                        focusable="false"
                        role="presentation"
                        class="tw-pointer-events-none icon icon-heart"
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M12.0057 19.5216L4.36699 12.6025C0.21554 8.45103 6.31817 0.48025 12.0057 6.92884C17.6931 0.48025 23.7681 8.47871 19.6443 12.6025L12.0057 19.5216Z"
                          stroke="black"
                          stroke-width="1.71429"
                          stroke-linecap="round"
                          stroke-linejoin="round"
                        ></path>
                      </svg>
                    </button>
                  </p>
                </div>
              </div>

              <p class="tw-text-sm ${Number(this.available_siblings) > 1 ? "" : "tw-hidden"}">${this.available_siblings} Colours</p>
            </a>
          `
              : html``
      }

      ${
          this.show_product_title == "true"
              ? html`
            <a href="/products/${this.productData.handle}" >
              <div class="tw-flex tw-justify-between tw-pt-3 tw-pb-6">
                <div class="tw-text-sm">
                  <div class="tw-pb-1 tw-max-w-[90%]">
                    ${this.productData.title}
                  </div>
                  <div class="tw-pb-1">
                    ${formatMoney(this.productData.price)}
                  </div>
                </div>
              </div>
            </a>
          `
              : html``
      }
    </div>`;
    }
}
